import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchLiabilities } from '../adminApi';
import { Panel } from '../ui/Panel';
import { Skeleton } from '../../ui/Skeleton';
import { LoadFailed } from '../ui/LoadState';
import { money } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { fromIso, fullDate, today } from '../../core/format/dates';
import { ADMIN_BASE } from '../../utils/hosts';
import type { RequestOptions } from '../../core/types/backend';
type Props = {
  on: string;
};

/**
 * Vooruitbetaald en openstaand op het eind van de periode: wat klanten al betaald hebben maar nog
 * niet kregen. Geen omzet, wel een schuld op de balans; de boekhouder vraagt dit elke maand
 * (docs/BOEKHOUDING.md §5). Regels: core/rules/liabilities.ts.
 */
export function LiabilitiesPanel({
  on: end
}: Props) {
  // Een periode die nog loopt: de stand van vandaag, niet van een dag die nog moet komen.
  const now = today();
  const on = end > now ? now : end;
  const load = useCallback((opts: RequestOptions) => fetchLiabilities(on, opts), [on]);
  const {
    data,
    status,
    reload
  } = useLatestLoad(load);
  return <Panel title="Vooruitbetaald en openstaand" description={`Stand op ${fullDate(fromIso(on))}. Geen omzet: dit hebben klanten al betaald, maar nog niet gekregen.`} data-id="element-3781" data-mp="2afrukdwih1e4">
      {status === 'error' && !data ? <LoadFailed variant="inline" title="Dit overzicht kon niet laden" onRetry={reload} data-id="element-3782" data-mp="21gy28zq7qx6d" /> : !data ? <Skeleton className="h-24 w-full" data-id="element-3783" data-mp="1wnlib2whbejy" /> : <dl className="divide-y divide-line text-[14px]" data-id="element-3784" data-mp="1rpto228d3gcl">
          <Row label="Cadeaubonnen" meta={plural(data.vouchers.count, 'bon met saldo', 'bonnen met saldo')} value={money(data.vouchers.cents)} to={`${ADMIN_BASE}/cadeaubonnen?status=actief`} data-id="element-3785" data-mp="1rqd0x4j5nejr" />
          <Row label="Vooruitbetaalde kuren" meta={`${plural(data.kuren.count, 'kuur', 'kuren')}, ${plural(data.kuren.sessions, 'sessie', 'sessies')} nog te geven`} value={money(data.kuren.cents)} data-id="element-3786" data-mp="1dq24zn2zn9rq" />
          <Row label="Klanttegoed" meta={data.credit.treatments > 0 ? `${plural(data.credit.customers, 'klant', 'klanten')}, plus ${plural(data.credit.treatments, 'behandeling', 'behandelingen')} tegoed` : plural(data.credit.customers, 'klant', 'klanten')} value={money(data.credit.cents)} data-id="element-3787" data-mp="185l4ukyn7y6v" />
          <div className="flex justify-between gap-4 pt-3 font-medium text-ink" data-id="element-3788" data-mp="1qtzr6uy0mo1c">
            <dt data-id="element-3789" data-mp="1d2l6mjogmes6">Totaal openstaand</dt>
            <dd className="tabular-nums" data-id="element-3790" data-mp="1d1jv6e3j7q9x">{money(data.vouchers.cents + data.kuren.cents + data.credit.cents)}</dd>
          </div>
        </dl>}
    </Panel>;
}
function Row({
  label,
  meta,
  value,
  to
}: {
  label: string;
  meta: string;
  value: string;
  to?: string;
}) {
  return <div className="flex items-baseline justify-between gap-4 py-2.5" data-id="element-3791" data-mp="2ak3aj11fbidd">
      <dt className="min-w-0" data-id="element-3792" data-mp="2jw9b4txuul44">
        {to ? <Link to={to} className="text-ink underline decoration-line underline-offset-4 hover:decoration-ink" data-id="element-3793" data-mp="2fvyvsqo6aicl">
            {label}
          </Link> : <span className="text-ink" data-id="element-3794" data-mp="2tmvn8e6d4ti2">{label}</span>}
        <span className="block text-[12px] text-muted" data-id="element-3795" data-mp="2onshssmh0l9k">{meta}</span>
      </dt>
      <dd className="shrink-0 tabular-nums text-ink" data-id="element-3796" data-mp="20yq1pp7hj0h6">{value}</dd>
    </div>;
}